import React, { useMemo, useState, useEffect, useRef } from "react";
import { Edit3, Layers, Ticket, Music, ShoppingBag, Utensils, Star, Calendar, ChevronDown, ChevronUp, Eye } from "lucide-react";
import {
  analyzeDayType,
  getAllRides,
  getAllShows,
  getAllRetail,
  hasDiningInfo,
  hasSpecialtyExperiences,
  getActivitiesByTimeBlock,
  sortActivitiesByTime,
} from "../../../../utils/itineraryDataProcessor";
import { parseDateForCompare } from "../../../../utils/dateUtils";

// Utility functions (copied from ItineraryView for self-contained component)
const formatDate = (d) => {
  if (!d) return "TBD";

  // Handle date strings that should be treated as local dates, not UTC
  let dt;
  if (typeof d === 'string' && d.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = d.split('-').map(Number);
    dt = new Date(year, month - 1, day);
  } else {
    dt = new Date(d);
  }

  if (isNaN(dt.getTime())) return "TBD";
  return dt.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

const formatHopperTitle = (parks) => {
  if (!parks || parks.length === 0) return "";
  if (parks.length <= 3) return parks.join(" & ");
  return `${parks.slice(0, 3).join(" • ")} +${parks.length - 3} more`;
};

const hasValidDayDate = (info) => {
  if (!info?.date) return false;
  return parseDateForCompare(info.date) !== null;
};

const getDayParks = (info) => {
  // Handle null/undefined info
  if (!info) {
    return [];
  }

  // New structure with parks array
  if (info.parks && Array.isArray(info.parks)) {
    return info.parks;
  }
  
  // Fallback to legacy structure
  const t = analyzeDayType(info);
  if (t.hopperParks?.length) return t.hopperParks;
  const p = info.park || info.Park;
  return p ? [p] : [];
};

// Build day preview string (collapsed state only)
const buildDayPreview = (day, isArrival, isDeparture, magicMessage) => {
  // For arrival/departure: show magic message snippet
  if (isArrival || isDeparture) {
    if (magicMessage && typeof magicMessage === 'string') {
      // Truncate to one line, max ~60 chars
      return magicMessage.length > 60 ? `${magicMessage.substring(0, 57)}...` : magicMessage;
    }
    return "";
  }
  
  // For normal park days: build from time blocks and dining
  const timeBlocks = getActivitiesByTimeBlock(day);
  const previews = [];
  
  // Check morning activities
  if (timeBlocks.morning && timeBlocks.morning.length > 0) {
    const sorted = sortActivitiesByTime(timeBlocks.morning);
    const firstActivity = sorted[0];
    const activityName = typeof firstActivity === 'string' 
      ? firstActivity 
      : (firstActivity?.name || 'Activity');
    previews.push(`Morning: ${activityName}`);
  }
  
  // Check lunch (from dining object)
  const lunch = day?.dining?.lunch || day?.lunch;
  if (lunch) {
    const lunchName = typeof lunch === 'string' ? lunch : (lunch?.name || 'Lunch');
    if (lunchName && lunchName !== 'Lunch not specified') {
      previews.push(`Lunch: ${lunchName}`);
    }
  }
  
  // Check afternoon activities
  if (previews.length < 2 && timeBlocks.afternoon && timeBlocks.afternoon.length > 0) {
    const sorted = sortActivitiesByTime(timeBlocks.afternoon);
    const firstActivity = sorted[0];
    const activityName = typeof firstActivity === 'string' 
      ? firstActivity 
      : (firstActivity?.name || 'Activity');
    previews.push(`Afternoon: ${activityName}`);
  }
  
  // Check dinner (from dining object)
  if (previews.length < 2) {
    const dinner = day?.dining?.dinner || day?.dinner;
    if (dinner) {
      const dinnerName = typeof dinner === 'string' ? dinner : (dinner?.name || 'Dinner');
      if (dinnerName && dinnerName !== 'Dinner not specified') {
        previews.push(`Dinner: ${dinnerName}`);
      }
    }
  }
  
  // Check evening/nighttime activities
  if (previews.length < 2 && timeBlocks.evening && timeBlocks.evening.length > 0) {
    const sorted = sortActivitiesByTime(timeBlocks.evening);
    const firstActivity = sorted[0];
    const activityName = typeof firstActivity === 'string' 
      ? firstActivity 
      : (firstActivity?.name || 'Activity');
    previews.push(`Night: ${activityName}`);
  }
  
  if (previews.length === 0) return "";
  
  const previewText = previews.join(' • ');
  // Truncate if too long (max ~80 chars for one line)
  return previewText.length > 80 ? `${previewText.substring(0, 77)}...` : previewText;
};

// Get activity badges for a day based on new data structure
const getActivityBadges = (day) => {
  const badges = [];
  
  // Count rides/attractions using new utility function
  const rides = getAllRides(day);
  if (rides.length > 0) {
    badges.push({ 
      type: 'rides', 
      count: rides.length, 
      icon: Ticket, 
      color: 'blue' 
    });
  }
  
  // Count shows using new utility function
  const shows = getAllShows(day);
  if (shows.length > 0) {
    badges.push({ 
      type: 'shows', 
      count: shows.length, 
      icon: Music, 
      color: 'purple' 
    });
  }
  
  // Count retail/shopping
  const retail = getAllRetail(day);
  if (retail.length > 0) {
    badges.push({ 
      type: 'shopping', 
      count: retail.length, 
      icon: ShoppingBag, 
      color: 'pink' 
    });
  }
  
  // Count dining
  if (hasDiningInfo(day)) {
    badges.push({ 
      type: 'dining', 
      count: 1, 
      icon: Utensils, 
      color: 'green' 
    });
  }
  
  // Count specialty experiences
  if (hasSpecialtyExperiences(day)) {
    badges.push({ 
      type: 'experiences', 
      count: 1, 
      icon: Star, 
      color: 'orange' 
    });
  }
  
  return badges;
};

// Memoized components for performance
const DayQuickSummary = React.memo(({ day }) => {
  const badges = useMemo(() => getActivityBadges(day), [day]);
  
  if (badges.length === 0) {
    return null;
  }
  
  return (
    <div className="flex flex-wrap gap-1.5">
      {badges.map((badge, idx) => {
        const Icon = badge.icon;
        const colorClasses = {
          blue: 'bg-blue-100 text-blue-800 border-blue-200',
          green: 'bg-green-100 text-green-800 border-green-200',
          purple: 'bg-purple-100 text-purple-800 border-purple-200',
          orange: 'bg-orange-100 text-orange-800 border-orange-200',
          pink: 'bg-pink-100 text-pink-800 border-pink-200',
        };
        
        return (
          <div
            key={idx}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${colorClasses[badge.color]}`}
          >
            <Icon className="w-3 h-3" />
            <span>{badge.count} {badge.type}</span>
          </div>
        );
      })}
    </div>
  );
});

// Accordion Day Item Component
const AccordionDayItem = React.memo(({ day, index, onViewDetails, onEditDay, magicMessages, isOpen, onToggle, initialOpen }) => {
  const t = useMemo(() => analyzeDayType(day), [day]);
  const parks = useMemo(() => getDayParks(day), [day]);
  const hopper = parks.length > 1;
  const arrival = t.isArrival;
  const departure = t.isDeparture;
  const contentRef = useRef(null);
  const itemRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  // Calculate content height for smooth animation
  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [isOpen, day]);

  // Auto-open if this is the initial open day (only on mount)
  useEffect(() => {
    if (initialOpen && !isOpen) {
      onToggle();
      // Scroll into view smoothly after a brief delay to allow render
      setTimeout(() => {
        if (itemRef.current) {
          itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - initialOpen is stable

  // Get time block summaries for expanded view
  const timeBlockSummaries = useMemo(() => {
    if (arrival || departure) return null;
    
    const timeBlocks = getActivitiesByTimeBlock(day);
    const summaries = [];
    
    if (timeBlocks.morning.length > 0) {
      const sorted = sortActivitiesByTime(timeBlocks.morning);
      summaries.push({
        title: 'Morning',
        activities: sorted.slice(0, 3).map(a => {
          if (typeof a === 'string') return a;
          return a.name || 'Activity';
        }).join(' • ')
      });
    }
    
    if (timeBlocks.afternoon.length > 0) {
      const sorted = sortActivitiesByTime(timeBlocks.afternoon);
      summaries.push({
        title: 'Afternoon',
        activities: sorted.slice(0, 3).map(a => {
          if (typeof a === 'string') return a;
          return a.name || 'Activity';
        }).join(' • ')
      });
    }
    
    if (timeBlocks.evening.length > 0) {
      const sorted = sortActivitiesByTime(timeBlocks.evening);
      summaries.push({
        title: 'Evening',
        activities: sorted.slice(0, 3).map(a => {
          if (typeof a === 'string') return a;
          return a.name || 'Activity';
        }).join(' • ')
      });
    }
    
    return summaries.length > 0 ? summaries : null;
  }, [day, arrival, departure]);

  const dayTitle = arrival 
    ? `Day ${index + 1} - Arrival Day` 
    : departure 
    ? `Day ${index + 1} - Departure Day`
    : hopper
    ? `Day ${index + 1} - ${formatHopperTitle(parks)}`
    : `Day ${index + 1} - ${parks[0] || `Day ${index + 1}`}`;

  // Build preview line for collapsed state
  const previewText = useMemo(() => {
    if (isOpen) return null; // Only show when collapsed
    const magicMsg = arrival ? magicMessages.arrival : departure ? magicMessages.departure : null;
    return buildDayPreview(day, arrival, departure, magicMsg);
  }, [day, arrival, departure, magicMessages, isOpen]);

  const badges = useMemo(() => getActivityBadges(day), [day]);

  // Determine accent bar color for expanded state
  const accentBarColor = isOpen 
    ? arrival 
      ? 'bg-yellow-400' 
      : departure 
      ? 'bg-gray-400' 
      : 'bg-blue-500'
    : '';

  return (
    <div
      ref={itemRef}
      id={`day-${index}`}
      className={`relative overflow-hidden transition-all duration-200 ${
        isOpen 
          ? 'shadow-sm bg-gray-50/50' 
          : 'shadow-sm border-l-[3px] border-l-transparent hover:border-l-gray-300 hover:bg-gray-50/30'
      } ${
        arrival || departure
          ? "bg-gradient-to-br from-yellow-50/35 to-orange-50/35"
          : "bg-white"
      }`}
    >
      {/* Left accent bar - expanded only */}
      {isOpen && (
        <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${accentBarColor}`} aria-hidden="true" />
      )}
      {/* Accordion Header - Always Visible */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset focus:ring-offset-0"
        aria-expanded={isOpen}
        aria-controls={`day-${index}-content`}
      >
        {/* 3-column grid layout: Left | Middle | Right */}
        <div className="grid grid-cols-[1fr_auto_auto] gap-3 items-center md:grid-cols-[1fr_auto_auto]">
          {/* Left: Day Title + Date */}
          <div className="min-w-0">
            <div className="flex gap-2 items-center">
              <h3 
                className="text-sm font-semibold text-gray-900 truncate"
                title={dayTitle}
              >
                {dayTitle}
              </h3>
              {/* Status badges - inline with title */}
              {(day?.regenerated || arrival || departure || (hopper && !arrival && !departure)) && (
                <div className="flex flex-shrink-0 gap-1">
                  {day?.regenerated && (
                    <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium text-orange-800 bg-orange-100 rounded-full border border-orange-200" title={`Regenerated on ${new Date(day.regeneratedAt).toLocaleDateString()}`}>
                      <svg className="mr-0.5 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                      </svg>
                      Regenerated
                    </span>
                  )}
                  {arrival && (
                    <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium text-gray-800 bg-gray-100 rounded-full border border-gray-300">
                      Arrival
                    </span>
                  )}
                  {departure && (
                    <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium text-gray-800 bg-gray-100 rounded-full border border-gray-300">
                      Departure
                    </span>
                  )}
                  {hopper && !arrival && !departure && (
                    <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium text-teal-800 bg-teal-100 rounded-full border border-teal-200">
                      Hopper
                    </span>
                  )}
                </div>
              )}
            </div>
            
            {/* Date */}
            <div className="flex gap-1.5 items-center mt-0.5 text-xs text-gray-500">
              <Calendar className="w-3 h-3" />
              <span>{formatDate(day?.date)}</span>
              {!hasValidDayDate(day) && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                  Date TBD
                </span>
              )}
            </div>
            
            {/* Preview line - only when collapsed */}
            {!isOpen && previewText && (
              <div className="mt-1.5 text-xs text-gray-400 leading-5 truncate" title={previewText}>
                {previewText}
              </div>
            )}
          </div>
          
          {/* Middle: Activity badges (Group A) */}
          <div className="flex-shrink-0 hidden sm:flex items-center">
            <div className="flex flex-wrap gap-1 items-center">
              {badges.slice(0, 3).map((badge, idx) => {
                const Icon = badge.icon;
                const colorClasses = {
                  blue: 'bg-blue-100 text-blue-800 border-blue-200',
                  green: 'bg-green-100 text-green-800 border-green-200',
                  purple: 'bg-purple-100 text-purple-800 border-purple-200',
                  orange: 'bg-orange-100 text-orange-800 border-orange-200',
                  pink: 'bg-pink-100 text-pink-800 border-pink-200',
                };
                
                return (
                  <div
                    key={idx}
                    className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium border ${colorClasses[badge.color]}`}
                    title={`${badge.count} ${badge.type}`}
                  >
                    <Icon className="w-3 h-3" />
                    <span className="hidden md:inline">{badge.count}</span>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Right: Action cluster + Chevron */}
          <div className="flex gap-3 items-center flex-shrink-0">
            {/* Group A: Activity badges (fallback for when middle is hidden) */}
            
            {/* Group B: Action buttons - with divider before actions */}
            <div className="flex gap-1 items-center relative pl-3 before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-px before:h-4 before:bg-gray-200">
              <button
                className="inline-flex items-center justify-center p-0.5 min-w-[28px] min-h-[28px] text-gray-600 bg-white border border-gray-300 rounded-md transition-colors hover:bg-gray-50 hover:text-blue-600 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewDetails({ ...day, index });
                }}
                title="View Details"
                aria-label="View Details"
              >
                <Eye className="w-3 h-3" />
              </button>
              {!arrival && !departure && (
                <button
                  className="inline-flex items-center justify-center p-0.5 min-w-[28px] min-h-[28px] text-gray-600 bg-white border border-gray-300 rounded-md transition-colors hover:bg-gray-50 hover:text-green-600 hover:border-green-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditDay({ ...day, index });
                  }}
                  title="Edit Day"
                  aria-label="Edit Day"
                >
                  <Edit3 className="w-3 h-3" />
                </button>
              )}
            </div>
            
            {/* Chevron Icon - aligned with title baseline */}
            <div className="flex-shrink-0">
              <ChevronDown 
                className={`w-3.5 h-3.5 text-gray-400 transition-all duration-200 ${
                  isOpen 
                    ? 'rotate-180 opacity-100' 
                    : 'opacity-60 hover:opacity-100'
                }`}
              />
            </div>
          </div>
        </div>
        
        {/* Mobile: Badges below (when collapsed only) */}
        {!isOpen && (
          <div className="flex sm:hidden gap-1.5 items-center mt-2 flex-wrap">
            {badges.slice(0, 4).map((badge, idx) => {
              const Icon = badge.icon;
              const colorClasses = {
                blue: 'bg-blue-100 text-blue-800 border-blue-200',
                green: 'bg-green-100 text-green-800 border-green-200',
                purple: 'bg-purple-100 text-purple-800 border-purple-200',
                orange: 'bg-orange-100 text-orange-800 border-orange-200',
                pink: 'bg-pink-100 text-pink-800 border-pink-200',
              };
              
              return (
                <div
                  key={idx}
                  className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium border ${colorClasses[badge.color]}`}
                >
                  <Icon className="w-3 h-3" />
                  <span>{badge.count} {badge.type}</span>
                </div>
              );
            })}
          </div>
        )}
      </button>

      {/* Accordion Content - Expandable */}
      <div
        id={`day-${index}-content`}
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          maxHeight: isOpen ? `${contentHeight}px` : '0px',
          opacity: isOpen ? 1 : 0,
        }}
      >
        <div ref={contentRef} className="px-4 pb-4 pt-3 border-t border-gray-200/60">
          {/* Special message for arrival/departure days */}
          {(arrival || departure) && (
            <div className="p-3 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg border border-yellow-200/60 shadow-sm">
              <div className="text-sm leading-relaxed text-yellow-900">
                {arrival ? magicMessages.arrival : magicMessages.departure}
              </div>
            </div>
          )}

          {/* Time block summaries for regular days */}
          {timeBlockSummaries && (
            <div className="mt-3 space-y-2">
              {timeBlockSummaries.map((block, idx) => (
                <div key={idx} className="p-2.5 bg-gray-50/80 rounded-lg border border-gray-200/60">
                  <div className="mb-1 text-xs font-semibold text-gray-700 uppercase tracking-wide">{block.title}</div>
                  <div className="text-sm leading-relaxed text-gray-600">{block.activities || 'No activities scheduled'}</div>
                </div>
              ))}
            </div>
          )}

          {/* Action buttons - Right aligned */}
          <div className="flex gap-2 justify-end mt-4">
            <button
              className="inline-flex gap-1.5 items-center px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-md border border-blue-200 transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              onClick={(e) => {
                e.stopPropagation();
                onViewDetails({ ...day, index });
              }}
            >
              <Layers className="w-4 h-4" />
              View Details
            </button>
            {!arrival && !departure && (
              <button
                className="inline-flex gap-1.5 items-center px-3 py-1.5 text-sm font-medium text-green-600 bg-green-50 rounded-md border border-green-200 transition-colors hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditDay({ ...day, index });
                }}
              >
                <Edit3 className="w-4 h-4" />
                Edit
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

/**
 * ItineraryDaysOverviewAccordion - Accordion layout for displaying days
 * Single-open accordion with smooth animations and keyboard accessibility
 */
const ItineraryDaysOverviewAccordion = ({ 
  filteredDays, 
  onViewDetails, 
  onEditDay, 
  magicMessages,
  selectedDayIndex = null, // Optional: day index to auto-open
}) => {
  const [openIndex, setOpenIndex] = useState(() => {
    // Auto-open selected day if provided
    if (selectedDayIndex !== null && selectedDayIndex >= 0 && selectedDayIndex < filteredDays.length) {
      return selectedDayIndex;
    }
    return null;
  });

  const handleToggle = (index) => {
    setOpenIndex(prev => prev === index ? null : index);
  };

  return (
    <>
      {/* Accordion Layout - Unified container */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="divide-y divide-gray-100">
          {filteredDays.map((day, index) => (
            <AccordionDayItem
              key={`${day?.date || 'unknown'}-${index}`}
              day={day}
              index={index}
              onViewDetails={onViewDetails}
              onEditDay={onEditDay}
              magicMessages={magicMessages}
              isOpen={openIndex === index}
              onToggle={() => handleToggle(index)}
              initialOpen={selectedDayIndex === index}
            />
          ))}
        </div>
      </div>

      {/* Empty State - handled by parent component */}
    </>
  );
};

export default ItineraryDaysOverviewAccordion;
